import React, { useEffect } from "react";
import { BlockNoteView } from "@blocknote/mantine";
import { useCreateBlockNote } from "@blocknote/react";
import type { Block } from "@blocknote/core";
import { joinDictatedText } from "../../../domain/dictationText";
import "@blocknote/core/fonts/inter.css";
import "@blocknote/mantine/style.css";

// ─── Types ───────────────────────────────────────────────────────────────────
interface NotionEditorProps {
    placeholder?: string;
    value?: string;
    onChange?: (value: string) => void;
    className?: string;
    disabled?: boolean;
    error?: boolean;
    hint?: string;
    borderless?: boolean;
}

export interface NotionEditorHandle {
    /**
     * Show what is being heard right now, as provisional text the therapist
     * watches form. Each call replaces what the previous one wrote; "" clears it.
     */
    setInterimText: (text: string) => void;
    /**
     * Settle the dictated phrase: the provisional text is *replaced* by
     * `finalText`, never followed by it. Appending instead of replacing is what
     * wrote every spoken sentence into the note twice.
     */
    commitInterim: (finalText?: string) => void;
}

/** The part of the underlying TipTap editor the dictation writes through. */
type TiptapTransaction = { insertText: (text: string, at: number) => void };

type TiptapChain = {
    command: (run: (props: { tr: TiptapTransaction }) => boolean) => TiptapChain;
    setTextSelection: (position: number) => TiptapChain;
    run: () => boolean;
};

type TiptapLike = {
    state: {
        selection: { from: number };
        doc: {
            content: { size: number };
            textBetween: (
                from: number,
                to: number,
                blockSeparator?: string,
                leafText?: string
            ) => string;
        };
    };
    commands: { deleteRange: (range: { from: number; to: number }) => boolean };
    chain: () => TiptapChain;
};

/** Where the provisional text sits, and what it says, so it can be replaced. */
type InterimText = { from: number; to: number; text: string };

/** How much of the note before the insertion point the spacing decision needs. */
const PRECEDING_CONTEXT_CHARS = 20;

/** The text just before the insertion point, for spacing and capitalisation. */
function textBefore(tiptap: TiptapLike, at: number): string {
    try {
        return tiptap.state.doc.textBetween(
            Math.max(0, at - PRECEDING_CONTEXT_CHARS),
            at,
            "\n",
            " "
        );
    } catch {
        return "";
    }
}

/**
 * Takes the provisional text back out of the note, returning where it was.
 *
 * Null when there is nothing to remove — or when what sits there is no longer
 * what was written, which means the therapist has been editing around it and
 * deleting the range would eat their words.
 */
function dropInterim(tiptap: TiptapLike, interim: InterimText | null): number | null {
    if (!interim) return null;
    const { from, to, text } = interim;
    try {
        if (to > tiptap.state.doc.content.size) return null;
        if (tiptap.state.doc.textBetween(from, to, "\n", " ") !== text) return null;
        tiptap.commands.deleteRange({ from, to });
        return from;
    } catch {
        return null;
    }
}

/**
 * Writes plain text at a position.
 *
 * Plain, rather than TipTap's `insertContent`, which parses what it is given as
 * HTML: that swallowed the leading space of every dictated phrase — running the
 * words together — and would mangle a dictated "&" or "<".
 */
function writeText(tiptap: TiptapLike, at: number, text: string): boolean {
    try {
        return tiptap
            .chain()
            .command(({ tr }) => {
                tr.insertText(text, at);
                return true;
            })
            .setTextSelection(at + text.length)
            .run();
    } catch {
        return false;
    }
}

// Parse stored value: JSON blocks (new) or HTML (legacy)
async function parseValue(editor: ReturnType<typeof useCreateBlockNote>, value: string): Promise<Block[]> {
    if (!value) return [];
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed as Block[];
    } catch {
        // fall through to HTML parsing
    }
    return editor.tryParseHTMLToBlocks(value);
}

// ─── Main Component ───────────────────────────────────────────────────────────
const NotionEditor = React.forwardRef<NotionEditorHandle, NotionEditorProps>(({
    value = "",
    onChange,
    className = "",
    disabled = false,
    error = false,
    hint = "",
    borderless = false,
}, ref) => {
    const editor = useCreateBlockNote();
    const interimRef = React.useRef<InterimText | null>(null);

    /**
     * Writes `text` where the dictation is going: over the provisional text if
     * there is any, otherwise at the caret. Returns where it landed so the
     * provisional text can be tracked and replaced next time.
     */
    const writeDictation = React.useCallback(
        (text: string): InterimText | null => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const tiptap = (editor as any)?._tiptapEditor as TiptapLike | undefined;
            if (!tiptap) return null;

            const previous = interimRef.current;
            interimRef.current = null;
            const at = dropInterim(tiptap, previous) ?? tiptap.state.selection.from;

            const written = joinDictatedText(textBefore(tiptap, at), text);
            if (!written) return null;
            if (!writeText(tiptap, at, written)) return null;

            return { from: at, to: at + written.length, text: written };
        },
        [editor]
    );

    React.useImperativeHandle(
        ref,
        () => ({
            setInterimText: (text: string) => {
                interimRef.current = writeDictation(text);
            },
            commitInterim: (finalText: string = "") => {
                // Deliberately not tracked: the phrase has settled and is part
                // of the note now, so the next phrase must not overwrite it.
                writeDictation(finalText);
            },
        }),
        [writeDictation]
    );

    const isSyncingRef = React.useRef(false);
    const lastExternalValueRef = React.useRef<string | null>(null);

    useEffect(() => {
        let isMounted = true;

        if (value !== lastExternalValueRef.current) {
            async function sync() {
                if (editor) {
                    isSyncingRef.current = true;
                    const blocks = await parseValue(editor, value);
                    if (isMounted) {
                        editor.replaceBlocks(editor.document, blocks);
                        lastExternalValueRef.current = value;
                        setTimeout(() => {
                            if (isMounted) isSyncingRef.current = false;
                        }, 50);
                    } else {
                        isSyncingRef.current = false;
                    }
                }
            }
            sync();
        }

        return () => {
            isMounted = false;
            isSyncingRef.current = false;
        };
    }, [value, editor]);

    const handleChange = () => {
        if (!onChange || !editor || isSyncingRef.current) return;
        let json: string;
        try {
            json = JSON.stringify(editor.document);
        } catch {
            return;
        }
        lastExternalValueRef.current = json;
        onChange(json);
    };

    const borderClass = borderless
        ? "border-transparent shadow-none"
        : error
            ? "border-red-400 ring-1 ring-red-400/30"
            : "border-gray-200 focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-400/20";

    const bgClass = borderless
        ? "bg-transparent"
        : disabled
            ? "bg-gray-50 opacity-60 cursor-not-allowed"
            : "bg-white";

    return (
        <div className={`relative ${className}`}>
            <div className={`rounded-xl border transition-all duration-200 overflow-hidden min-h-[120px] ${borderClass} ${bgClass}`}>
                <div className="p-2">
                    <BlockNoteView
                        editor={editor}
                        editable={!disabled}
                        onChange={handleChange}
                        theme="light"
                        formattingToolbar={!disabled}
                        slashMenu={!disabled}
                    />
                </div>
            </div>
            {hint && (
                <p className={`mt-1.5 text-xs ${error ? "text-red-500" : "text-gray-500"}`}>
                    {hint}
                </p>
            )}
        </div>
    );
});

export default NotionEditor;
